import React, { useState, useEffect } from "react";
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';

import { personalInfo } from "./lib/ui";

import Loader from "./components/Loader";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Education from "./components/Education";
import Hobbies from "./components/Hobbies";

import Section from "./components/Section";
import Services from "./components/Services";
import Skills from "./components/Skills";
import SocialButtons from "./components/SocialButtons";

export default function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative min-h-screen text-app-text font-body overflow-x-hidden">

      {/* ── Animated base gradient ── */}
      <div className="fixed inset-0 animate-bg-drift -z-30" />

      {/* ── Dot-mesh overlay ── */}
      <div className="fixed inset-0 bg-mesh -z-20 pointer-events-none" />

      {/* ── Floating glow orbs ── */}
      <div className="fixed inset-0 overflow-hidden -z-10 pointer-events-none">
        {/* Emerald orb – top left */}
        <div className="absolute top-[-5%] left-[-8%] w-[250px] h-[250px] sm:w-[600px] sm:h-[600px] bg-app-accent/5 rounded-full blur-[60px] sm:blur-[140px] animate-orb animate-orb-pulse" />
        {/* Teal orb – bottom right */}
        <div className="absolute bottom-[-10%] right-[-10%] w-[300px] h-[300px] sm:w-[700px] sm:h-[700px] bg-app-secondary/5 rounded-full blur-[80px] sm:blur-[160px] animate-orb-alt animate-orb-pulse" />
        {/* Secondary orbs - hidden on mobile for performance */}
        <div className="absolute top-[40%] right-[10%] w-[400px] h-[400px] bg-app-accent/7 rounded-full blur-[100px] animate-orb-delay hidden sm:block" />
        <div className="absolute top-[15%] right-[5%] w-[300px] h-[300px] bg-app-secondary/8 rounded-full blur-[80px] animate-orb animate-orb-pulse hidden sm:block" />
        <div className="absolute bottom-[20%] left-[5%] w-[350px] h-[350px] bg-app-accent/7 rounded-full blur-[90px] animate-orb-alt hidden sm:block" />
      </div>

      <Loader isLoading={loading} />
      <Navbar />

      <main>
        {/* Hero */}
        <Hero />

        {/* What I Do */}
        <Section
          id="services"
          title="Professional Focus"
          subtitle="Core areas of expertise and professional commitment"
          number="01"
        >
          <Services />
        </Section>



        {/* Skills */}
        <Section
          id="skills"
          title="Professional Arsenal"
          subtitle="Technical skills and tools I leverage for organizational success"
          number="02"
        >
          <Skills />
        </Section>

        {/* About Me */}
        <Section
          id="about"
          title="Background & Persona"
          subtitle="My academic foundation and personal interests"
          number="03"
          dark
        >
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <div className="glass-card rounded-xl3 p-6 md:p-8 hover-glow transition-all duration-300">
              <h3 className="font-display text-lg font-semibold text-app-text mb-6 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-app-accent/10 border border-app-accent/20 flex items-center justify-center text-app-accent text-sm">🎓</span>
                Education
              </h3>
              <Education />
            </div>
            <div className="glass-card rounded-xl3 p-6 md:p-8 hover-glow-v transition-all duration-300">
              <h3 className="font-display text-lg font-semibold text-app-text mb-6 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-app-secondary/10 border border-app-secondary/20 flex items-center justify-center text-app-secondary text-sm">⚡</span>
                Hobbies & Interests
              </h3>
              <Hobbies />
            </div>
          </div>

          {/* Contact */}
          <div id="contact" className="mt-16 sm:mt-24">
            <div className="glass-card rounded-[40px] p-8 md:p-12 lg:p-16 overflow-hidden relative flex flex-col lg:flex-row items-center justify-between gap-10">
              <div className="absolute inset-0 bg-gradient-to-br from-app-accent/5 to-app-secondary/5 pointer-events-none" />
              <div className="absolute -top-32 -left-32 w-96 h-96 bg-app-accent/10 rounded-full blur-[100px] pointer-events-none" />
              
              <div className="relative z-10 w-full lg:w-1/2 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-app-panel/60 backdrop-blur-md border border-app-border shadow-soft text-app-accent text-xs font-bold uppercase tracking-wider mb-8">
                  <span className="w-2 h-2 rounded-full bg-app-accent animate-pulse" />
                  {personalInfo.availability}
                </div>
                <h3 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-app-text mb-6 leading-tight">
                  Let's Connect <br className="hidden lg:block" />
                  <span className="text-gradient-cyan">And Collaborate.</span>
                </h3>
                <p className="text-app-mute text-base md:text-lg max-w-md mx-auto lg:mx-0 leading-relaxed">
                  I'm always open to discussing professional opportunities, innovative ideas, or ways to contribute to your organization.
                </p>
              </div>

              <div className="relative z-10 w-full lg:w-1/2">
                 <SocialButtons />
              </div>
            </div>
          </div>
        </Section>
      </main>

      {/* Footer */}
      <footer className="border-t border-app-border bg-app-panel/60 backdrop-blur-sm py-6">
        <p className="text-sm text-app-mute text-center">
          © {new Date().getFullYear()} <span className="text-app-text font-medium">{personalInfo.fullName}</span>. All rights reserved.
        </p>
      </footer>

      <Analytics />
      <SpeedInsights />
    </div>
  );
}
